/**
 * SoulInterviewVisualCard — chat-stream-native moodboard image card for the
 * adaptive soul-intake interview (#2058 per-turn visual layer).
 *
 * Drop-in: the lead's integrated interview chat surface renders this card when a
 * `intake:visual` push arrives for the current turn. It is NOT modal (the
 * boxed `SoulIntakePanel.tsx` it once shared a home with was removed —
 * crucible#2647), and carries no IPC calls of its own.
 *
 * Lifecycle:
 *  - Initially renders in a `loading` state with a shimmer placeholder (the main
 *    process is generating the image in parallel while the user reads the question).
 *  - Transitions to `ready` when `visual` prop is set (and its `.image` landed).
 *  - Transitions to `hidden` when `visual` is explicitly null (no visual expected
 *    for this turn).
 *  - Transitions to an explanatory failure state when a push arrives whose
 *    `.image` is null, with an explicit retry when the parent supplies one.
 *
 * The `data:` URI in `visual.image.dataUrl` is rendered directly — CSP-safe, no
 * external host required.
 *
 * #2646 — `visual.audio`, when present, renders a hidden `<audio autoPlay>` cue
 * (a music sting, not a loop) alongside the image/video. Same graceful-absence
 * contract as video: no `audio` field ⇒ no element, no chrome, nothing to notice.
 */
import React from 'react';
import type { SoulIntakeTurnVisual } from '@volley/crucible-ipc-types';

// crucible#2709 — ONE shared ceiling for both the vh (chat/phone) and cqh (tv)
// ready-state formulas below. A box large enough to be a real TV render or a
// full Preview-tab pane drives EITHER formula to this same maximum — that
// convergence is what keeps the real-TV/full-Preview render unchanged by the
// tv branch (only a genuinely small preview box, where cqh's lower floor
// actually bites, renders differently). Pulling it into one constant makes
// that convergence structural: nobody can edit one formula's ceiling without
// noticing it's shared. Verified empirically (Storybook + Playwright) at
// 1920x1080 — both branches render the image at this same height.
const VISUAL_CARD_MAX_HEIGHT_PX = 150;

// ── Props ────────────────────────────────────────────────────────────────────

export interface SoulInterviewVisualCardProps {
  /**
   * The visual payload pushed on `intake:visual`.
   *  - `undefined` = still generating (show shimmer placeholder).
   *  - `null`      = generation failed or flag-off (render nothing).
   *  - `SoulIntakeTurnVisual` = ready to display.
   */
  visual: SoulIntakeTurnVisual | null | undefined;
  /**
   * Turn index this card illustrates (0-based). Used for aria labelling
   * and keying the animation so each new question triggers a fresh fade-in.
   */
  questionIndex?: number;
  /**
   * crucible#2709 — `NorthStarCreateInterviewTv` mounts this card as a SIBLING
   * above `SoulInterviewTurnCard` inside the Develop-tab's small letterboxed
   * 16:9 DisplayPane, which is NOT proportional to the app viewport
   * (crucible#2672 measured it as low as ~180px tall at the pane's designed
   * 320px-wide floor). The default height below is `vh`-relative, correct for
   * the chat/phone mount (`IntegratedSoulInterview.tsx`) where the panel IS
   * the viewport. `tv` swaps to `cqh` — the nearest `container-type: size`
   * ancestor is the flex wrapper in `NorthStarCreateInterviewTv` that this
   * card and the turn card share — so the card shrinks with the box's TRUE
   * available height instead of claiming up to 150px of a box that might only
   * be 180px tall, starving the turn card below its own protected minimums
   * (crucible#2672) and reproducing the exact "content is never visible"
   * defect that fix addressed for the turn card, one level up the tree.
   */
  tv?: boolean;
  onRetry?: () => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export function SoulInterviewVisualCard({
  visual,
  questionIndex = 0,
  tv = false,
  onRetry,
}: SoulInterviewVisualCardProps): React.JSX.Element | null {
  // Flag-off renders nothing. A landed push whose image generation failed
  // renders a recoverable explanation. Only an ABSENT push (`undefined`)
  // means still-generating.
  if (visual === null) return null;
  if (visual?.image === null) {
    const failureCode = visual.attempt?.failureCode ?? 'provider-error';
    return (
      <div
        data-testid="soul-interview-visual-failed"
        role="status"
        className="flex min-h-20 w-full items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.025] px-4 py-3"
      >
        <div>
          <p className="text-sm font-medium text-white/75">Visual preview unavailable</p>
          <p className="mt-1 text-xs text-white/45">
            {failureCode === 'timeout'
              ? 'Generation took too long.'
              : failureCode === 'unavailable'
                ? 'The image provider is unavailable.'
                : failureCode === 'invalid-output'
                  ? 'The provider returned an unusable image.'
                  : 'The image provider could not finish this attempt.'}
          </p>
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="shrink-0 rounded-lg border border-white/20 px-3 py-2 text-xs font-medium text-white/80 hover:border-white/35"
          >
            Retry visual
          </button>
        )}
      </div>
    );
  }

  const isLoading = visual === undefined;

  return (
    <div
      data-testid="soul-interview-visual-card"
      data-question-index={questionIndex}
      // crucible#2709 — test-observability only (mirrors data-selected /
      // data-ready / data-consumer-surface elsewhere in this file): happy-dom
      // silently drops `height: clamp(...)` as an unparseable value (even for
      // the pre-existing vh branch), so a test can't recover the vh-vs-cqh
      // choice from `.style.height`. This attribute is inert for CSS/layout.
      data-visual-sizing={tv ? 'cqh' : 'vh'}
      style={{
        ...containerStyle,
        height: tv
          ? isLoading
            ? 'clamp(18px, 8cqh, 48px)'
            : `clamp(28px, 18cqh, ${VISUAL_CARD_MAX_HEIGHT_PX}px)`
          : isLoading
            ? '56px'
            : `clamp(96px, 18vh, ${VISUAL_CARD_MAX_HEIGHT_PX}px)`,
      }}
    >
      {isLoading ? (
        <ShimmerPlaceholder />
      ) : (
        <RevealImage
          dataUrl={visual.image.dataUrl}
          alt={visual.image.alt}
          questionIndex={questionIndex}
        />
      )}
      {!isLoading && visual.video && (
        <WowBeatVideo dataUrl={visual.video.dataUrl} alt={visual.video.alt} />
      )}
      {!isLoading && visual.audio && (
        <RevealAudio
          dataUrl={visual.audio.dataUrl}
          alt={visual.audio.alt}
          questionIndex={questionIndex}
        />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

// 2026-07-10 — the spinner-only shimmer read as an unexplained, possibly-
// broken blank bar (Sean: "this looks broken. not sure what it is."). The
// "Generating moodboard image…" label existed only as an aria-label — real
// for screen readers, invisible to anyone actually looking at the screen.
// A short visible caption next to the spinner (not below it — the card is
// only 18-56px tall, far too short for a second text row) fixes that for
// sighted users without changing the shimmer's size/animation.
function ShimmerPlaceholder(): React.JSX.Element {
  return (
    <div data-testid="soul-visual-shimmer" role="status" style={shimmerStyle}>
      <span style={shimmerGlowStyle} aria-hidden="true" />
      <span style={shimmerLabelStyle}>Generating art…</span>
    </div>
  );
}

function RevealImage({
  dataUrl,
  alt,
  questionIndex,
}: {
  dataUrl: string;
  alt: string;
  questionIndex: number;
}): React.JSX.Element {
  return (
    <img
      key={`reveal-${questionIndex}`}
      data-testid="soul-visual-image"
      src={dataUrl}
      alt={alt}
      style={imageStyle}
      draggable={false}
    />
  );
}

function WowBeatVideo({ dataUrl, alt }: { dataUrl: string; alt: string }): React.JSX.Element {
  return (
    <video
      data-testid="soul-visual-video"
      src={dataUrl}
      aria-label={alt}
      style={imageStyle}
      autoPlay
      loop
      muted
      playsInline
    />
  );
}

/**
 * #2646 — the REVEAL music sting. Keyed by `questionIndex` (mirrors `RevealImage`)
 * so each new turn mounts a fresh element and autoplays cleanly rather than
 * re-triggering playback on an existing one via a `src` swap. ONE-SHOT (no
 * `loop`) and audible (no `muted`) — the opposite of the ambient wow-beat video.
 * Visually hidden: the sting is heard, not watched: no player chrome, no new
 * layout, so it doesn't compete with the image/video for the card's space.
 */
function RevealAudio({
  dataUrl,
  alt,
  questionIndex,
}: {
  dataUrl: string;
  alt: string;
  questionIndex: number;
}): React.JSX.Element {
  return (
    <audio
      key={`reveal-audio-${questionIndex}`}
      data-testid="soul-visual-audio"
      src={dataUrl}
      aria-label={alt}
      autoPlay
      style={visuallyHiddenStyle}
    />
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
// Inline styles: keeps the component self-contained with no CSS module dependency.

const containerStyle: React.CSSProperties = {
  width: '100%',
  borderRadius: '16px',
  overflow: 'hidden',
  background: '#0f1014',
  border: '1px solid rgba(255,255,255,0.08)',
  position: 'relative',
};

const imageStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const shimmerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  background: 'linear-gradient(90deg, #1a1c22 25%, #23262e 50%, #1a1c22 75%)',
  backgroundSize: '200% 100%',
  animation: 'soul-visual-shimmer 1.6s infinite',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  overflow: 'hidden',
};

const shimmerGlowStyle: React.CSSProperties = {
  width: '24px',
  height: '24px',
  flexShrink: 0,
  borderRadius: '50%',
  border: '2px solid rgba(255,255,255,0.12)',
  borderTopColor: 'var(--color-crucible-accent, #6366f1)',
  animation: 'soul-visual-spin 1s linear infinite',
};

// 2026-07-10 round 2 — uppercase + tracked + accent-tinted to match the
// app's other micro-labels (e.g. the "Interview - Question N" eyebrow in
// Workspace.tsx's legacy panel) instead of a plain gray caption, so this
// reads as part of the same designed surface rather than a leftover
// unstyled default.
const shimmerLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--color-crucible-accent, #6366f1)',
  whiteSpace: 'nowrap',
};

/** The audio sting is heard, not watched — render it off-screen rather than
 *  give it a chrome/player footprint on the card (no `display: none`, which
 *  some browsers use as a cue to skip loading/playing the resource). */
const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
